import { ChevronLeftIcon, EnterIcon } from "@radix-ui/react-icons";
import { Box, Button, Callout, Card, Flex, Spinner, Text } from "@radix-ui/themes";
import { useState } from "react";
import { Controller, useForm } from "react-hook-form";
import toast from "react-hot-toast";
import { useMutation, useQuery } from "react-query";
import { useSelector } from "react-redux";
import { Link, useNavigate } from "react-router-dom";
import { DragAndDropFileUpload } from "../../components/inputs/FileUpload/DragAndDropFileUpload";
import RadioGroupComponent from "../../components/inputs/RadioGroup/RadioGroupComponent";
import SelectComponent from "../../components/inputs/Select/SelectComponent";
import { useGetProcessModelsQuery } from "../../hooks/useGetProcessModelsQuery";
import { useGetSchoolBankConfigQuery } from "../../hooks/useGetSchoolBankConfigQuery";
import { useGetTeacheIdByUserId } from "../../hooks/useGetTeacheIdByUserId";
import { RootState } from "../../redux/store";
import { routes } from "../../routes";
import { createStudentDocument } from "../../services/school/student/createStudent";
import { DocumentTypeEnum, StateEnum, StudentDocumentType } from "../../types/Student";
import { getFile } from "../../utils/fileUtils";
import { stringToBoolean } from "../../utils/utilities";
import { CourseType } from "../../types/Course";
import { getStudentById, getStudentCoursesByStudentId } from "../../services/school/student/getStudentByCode";
import { removeDiacritics } from "../../utils/stringUtils";
import { createStudentDebt, getStudentDebt } from "../../services/finance/studentDebt";
import { createPaymentReference, getReferenceCodeV2 } from "../../services/finance/paymentReference";
import { createPaymentTransaction } from "../../services/finance/paymentTransaction";
import { useGetPaymentTypesQuery } from "../../hooks/useGetPaymentTypesQuery";
import { PaymentReferenceType, PaymentTransactionType, StudentDebtType } from "../../types/Finance";
import { addDaysToDate, formatDateForInput } from "../../utils/dateFormatter";
import { setPreviousPath } from "../../redux/slices/previousPath";
import { useAppDispatch } from "../../hooks/hooks";
import { getUserById } from "../../services/user/getUsers";
import { setStudentData } from "../../redux/slices/school/student";
import { setUserData } from "../../redux/slices/users/user";
import { setPaymentsTransactionsData } from "../../redux/slices/finance/paymentTransaction";
import { setStudentDebtData } from "../../redux/slices/finance/studentDebt";
import { setPdfPreviewType } from "../../redux/slices/report/report";
import MultiSelectComponent from "../../components/inputs/Select/MultiSelectComponent";
import { printInvoice } from "../finance/StudentDebt/printInvoice";
import { useExportDocument } from "../../hooks/useExportDocument";
import { useIES } from "../../hooks/useIES";
import { useLogoBase64 } from "../../hooks/useLogoBase64";
import { useEffect } from "react";
import { printReceipt } from "../finance/StudentDebt/printReceipt";


export const RequestStudentDocument = () => {

  const [currentPage, setCurrentPage] = useState(1);
  const mutation = useMutation({ mutationFn: createStudentDocument, mutationKey: 'createStudentDocument' });
  const dispatch = useAppDispatch();


  const { exportDocumentPdf } = useExportDocument();

  const { IES } = useIES();
  const { logoBase64 } = useLogoBase64();
  const loggedUserData = useSelector(
    (state: RootState) => state.login.userData
  );

  const mutationCreateStudentDebt = useMutation({
    mutationFn: createStudentDebt,
    mutationKey: "createStudentDebt",
  });
  const mutationSaveReferenceCode = useMutation({
    mutationFn: createPaymentReference,
    mutationKey: "createPaymentReference",
  });
  const mutationCreatePaymentTransaction = useMutation({
    mutationFn: createPaymentTransaction,
    mutationKey: "createPaymentTransaction",
  });
  const {
    data: paymentTypesData,
    status: paymentTypesStatus,
    isError: isErrorPaymentTypes,
    isLoading: isLoadingPaymentTypes,
  } = useGetPaymentTypesQuery({ PageNumber: 1, PageSize: 400 });
  const { UserId } = useSelector((state: RootState) => state.login.userData)
  const { data: studentData } = useGetTeacheIdByUserId(UserId, { enabled: !!UserId });
  const navigate = useNavigate();

  const { data: courses } = useQuery([], async () => {
    if (!studentData?.idStudent) return [];
    const courses = await getStudentCoursesByStudentId(studentData.idStudent!);
    return courses;
  }, {
    enabled: !!studentData?.idStudent,
    retry: true,
    refetchOnWindowFocus: true
  })
  const defaultValues: StudentDocumentType = {
    state: StateEnum.INITIATE,
    paymentProof: "",
    idStudent: studentData?.idStudent!,
    paymentProofFile: null,
    docType: "",
    paymentMethod: "",
    isUrgent: false,
    studentCode: "",
    isValid: false,
    payment: {
      isPaymentByEntityReference: true,
      paymentProof: '',
      idBankEntity: ''
    },
    courseId: "",
    modelType: ""
  }

  const { control, handleSubmit, formState: { errors }, getValues, setValue, watch, resetField } = useForm<StudentDocumentType>
    (
      {
        //resolver: yupResolver(CreateClassroomFormValidation),
        defaultValues,
        mode: 'onBlur',
        reValidateMode: 'onBlur',
        shouldUseNativeValidation: false,
      }
    );

  const watchedDocType = watch('docType');

  const { data: processModelsData, status: processModelsStatus } = useGetProcessModelsQuery(
    { PageNumber: currentPage, PageSize: currentPage, ProcessName: watchedDocType },
    {
      enabled: !!watchedDocType,
      refetchOnWindowFocus: false,
      retry: false
    }
  );

  const { data: schoolBankConfigsData, status: schoolBankConfigsStatus, isError: isErrorSchoolschoolBankConfigs, isLoading: isLoadingSchoolschoolBankConfigs } = useGetSchoolBankConfigQuery({ PageNumber: 1, PageSize: 400 });
  const schoolBankConfigsArray = schoolBankConfigsData?.items.map(i => ({
    bankEntity: i.bankEntity!,
    bankName: i.bankName!
  })) ?? [];
  const handleBack = () => {
    navigate(routes.studentDocuments);
  }
  const onSubmit = handleSubmit(async (data) => {
    try {
      if(!stringToBoolean(data.payment?.isPaymentByEntityReference)) {
        if (!data.payment?.paymentProof) {
          toast.error("Comprovativo de pagamento é obrigatório");
          return;
        }
      }
      data.isValid = stringToBoolean(data.payment?.isPaymentByEntityReference)
        ? false
        : true;
      data.idStudent = studentData?.idStudent!;
      data.madeBy = studentData?.idStudent;
      data.isUrgent = Boolean(data.isUrgent)
      data.madeDate = new Date().toISOString();
      const { level } = courses?.find(c => c.id === data.courseId) ?? {};
      data.modelType = `${removeDiacritics(data.docType.toUpperCase())}_${removeDiacritics((level ?? '').toUpperCase())}`;

      data.paymentProofFile = null;
      const resultRequestStudentDocument = await mutation.mutateAsync(data);

      if (!resultRequestStudentDocument) {
        toast.error("Erro ao enviar pedido");
        return;
      }
      let amountToPay = paymentTypesData?.items?.reduce((total, paymentType) => {
        if (Array.isArray(data.idsPaymentTypes)
          ? data.idsPaymentTypes.includes(paymentType.id!)
          : paymentType.id === data.idsPaymentTypes) {
          return total + (paymentType.amount || 0);
        }
        return total;
      }, 0);
      if (!amountToPay || amountToPay <= 0) {
        amountToPay = 0;
      }
      let studentDebtData: StudentDebtType = {
        idStudent: data.idStudent!,
        fullname: `${studentData?.idStudent!}`,
        idPaymentType: `${data.idsPaymentTypes?.at(0)}`,
        originalAmount: amountToPay,
        currentAmount: amountToPay,
        dueDate: formatDateForInput(addDaysToDate(new Date(), 30).toISOString()),
        description: `Pagamento de ${data.docType} - ${courses?.find(c => c.id === data.courseId)?.description}MT`,
        idStudentDocument: resultRequestStudentDocument,
        isPayed: stringToBoolean(data.payment?.isPaymentByEntityReference)
          ? false
          : true,
      }
      try {
        const resultCreateStudentDebt =
          await mutationCreateStudentDebt.mutateAsync(studentDebtData);
        studentDebtData = { ...studentDebtData, id: resultCreateStudentDebt };

        if (stringToBoolean(getValues("payment.isPaymentByEntityReference"))) {
          const currentDate = new Date();
          const referenceCode = await getReferenceCodeV2({
            amount: studentDebtData.currentAmount,
            idStudent: studentDebtData.idStudent,
            idPaymentType: studentDebtData.idPaymentType,
            idStudentDebt: studentDebtData.id,
            month: (currentDate.getMonth() + 1).toString(),
            year: currentDate.getFullYear().toString(),
            description: studentDebtData.description,
            idBankEntity: data?.payment?.idBankEntity,
            entity: String(
              schoolBankConfigsData?.items.find(
                (i) => i.id === getValues("payment.idBankEntity")
              )?.bankEntity
            ),
          });
          const paymentReference: PaymentReferenceType = {
            idStudent: studentDebtData.idStudent,
            idPaymentType: studentDebtData.idPaymentType,
            amount: studentDebtData.currentAmount,
            dueDate: studentDebtData.dueDate,
            referenceCode: referenceCode,
            idStudentDebt: studentDebtData.id,
            entity: String(
              schoolBankConfigsData?.items.find(
                (i) => i.id === getValues("payment.idBankEntity")
              )?.bankEntity
            ),
            idBankEntity: data?.payment?.idBankEntity,
          };
          await mutationSaveReferenceCode.mutateAsync(paymentReference);
        } else {
          if (data.payment && data.payment.paymentProof) {
            const { base64 } = await getFile(data.payment.paymentProof);
            data.paymentProof = base64;
            const paymentTransaction: PaymentTransactionType = {
              date: new Date().toISOString(),
              idPaymentType: `${data.idsPaymentTypes?.at(0)}`,
              amountPaid: amountToPay,
              idStudent: studentDebtData.idStudent,
              paymentProof: base64,
              idStudentDebt: resultCreateStudentDebt,
              idCompleted: true,
            };
            await mutationCreatePaymentTransaction.mutateAsync(
              paymentTransaction
            );
          }
        }
      } catch (error) {
        toast.error(`Erro ao criar ${studentDebtData.isPayed ? 'Recibo' : 'Factura'} do estudante`);
        console.error(error);
      }

      toast.success("Pedido enviado ");
      dispatch(setPreviousPath(routes.studentDocuments))

      if (stringToBoolean(getValues("payment.isPaymentByEntityReference"))) {
        await handlePrintInvoice(studentDebtData);
      } else {
        await handlePrintReceipt(studentDebtData);
      }

    } catch (err: any) {
      toast.error(`${mutation.error ?? err?.message}`);
    }
  });

  const handlePrintReceipt = async (c: StudentDebtType) => {
    if (!c.isPayed) {
      toast.error("Ainda não foi pago");
      return;
    }
    try {
      const studentDebtDataRequest = await getStudentDebt({ PageSize: 1, PageNumber: 1, Id: c.id });
      const paymentsTransactions = studentDebtDataRequest?.items
        .flatMap((sdr) => sdr.paymentTransactions || [])
        .filter((ref) => ref?.id)
        .filter((ref) => ref.status);
      const studentDebt = studentDebtDataRequest?.items.at(0)!
      try {
        const studentData = await getStudentById(studentDebt.idStudent);
        const userData = await getUserById(studentData.idUser)
        dispatch(setStudentData(studentData));
        dispatch(setUserData(userData));
      } catch (error) {
        console.error(error)
      }
      dispatch(setPaymentsTransactionsData(paymentsTransactions))
      dispatch(setStudentDebtData(studentDebt))
   await printReceipt({
                            studentDebtId: c.id!,
                            schoolBankConfigsData: schoolBankConfigsArray,
                            IES,
                            logoBase64: logoBase64 ?? undefined,
                            exportDocument: exportDocumentPdf,
                            dispatch,
                            loggedUserData: loggedUserData,
                          });
      navigate(routes.studentDocuments);
    } catch (error) {
      console.error(error)
    }
  }
  const handlePrintInvoice = async (c: StudentDebtType) => {
    try {
      if (c.isPayed) {
        toast.error("Não é possivel obter cobrança paga no momento");
        return;
      }
      const studentDebtDataRequest = await getStudentDebt({ PageSize: 1, PageNumber: 1, Id: c.id });
      const paymentReferences = studentDebtDataRequest?.items
        .flatMap((sdr) => sdr.paymentReferences || [])
        .filter((ref) => ref?.id)
        .filter((ref) => !ref?.isPayed)
        .filter((ref) => ref.status);
      let studentDebt = studentDebtDataRequest?.items?.at(0)!;
      const paymentReference = paymentReferences?.find(p => p.amount === studentDebt.currentAmount);
      if (paymentReference) {
        studentDebt.entityCode = paymentReference.entity;
        studentDebt.referenceCode = paymentReference.referenceCode;
        studentDebt.bankDescription = schoolBankConfigsData?.items.find(i => i.bankEntity == paymentReference.entity)?.bankDescription ?? schoolBankConfigsData?.items.find(i => i.bankEntity == paymentReference.entity)?.bankName ?? "";
        studentDebt.bankName = schoolBankConfigsData?.items.find(i => i.bankEntity == paymentReference.entity)?.bankName ?? "";
      }
      try {
        const studentData = await getStudentById(studentDebt.idStudent);
        const userData = await getUserById(studentData.idUser)
        studentDebt.fullname = studentData.fullname;
        dispatch(setStudentData(studentData));
        dispatch(setUserData(userData));
      } catch (error) {
        console.error(error)
      }
      await printInvoice({
        studentDebtId: studentDebt.id!,
        schoolBankConfigsData: schoolBankConfigsArray,
        IES,
        logoBase64: logoBase64 ?? undefined,
        exportDocument: exportDocumentPdf,
        loggedUserData: loggedUserData,
        dispatch,
      });
      navigate(routes.studentDocuments);
    } catch (error) {
      console.error(error)
    }
  }

  const handleChangeDocumentType = (docType: string) => {
    setValue('docType', docType);

    // const paymentType = stringToBoolean(getValues('payment.isPaymentByEntityReference'))
    // if (paymentType) {
    //   setValue('payment.idBankEntity', processModelsData?.items.at(0)?.idSchoolBankConfig);
    //   setValue(
    //     'idsPaymentTypes',
    //     processModelsData?.items.at(0)?.idsPaymentTypes
    //       ? processModelsData.items.at(0)?.idsPaymentTypes?.split(',') ?? []
    //       : []
    //   );
    // }
  }

  const handleOnChangePaymentType = (e: any) => {
    const paymentType = stringToBoolean(getValues('payment.isPaymentByEntityReference'))
    if (paymentType) {
      resetField('payment.paymentProof')
    }
    if (!paymentType) {
      resetField('payment.idBankEntity')
    }
  };

  const watchPaymentType = watch('payment.isPaymentByEntityReference');
  useEffect(() => {
    const model = processModelsData?.items?.[0];
    if (!model) return;

    setValue("payment.idBankEntity", model.idSchoolBankConfig ?? "");

    setValue(
      "idsPaymentTypes",
      model.idsPaymentTypes
        ? model.idsPaymentTypes.split(",").map(id => id.trim())
        : []
    );
  }, [processModelsData, setValue]);

  if (!studentData || !studentData.idStudent) {
    return (
      <Flex direction="column">
        <Callout.Root color="red" role="alert">
          <Text>
            Entre como estudante &nbsp;
            <Link to={routes.dashboard} style={{ color: 'var(--gray-12)' }}>Voltar</Link>
          </Text>
        </Callout.Root>

      </Flex>
    );
  }

  return (
    <form onSubmit={onSubmit} noValidate>
      <Card size='1'>
        <Flex direction='column' gap='4'>

          <SelectComponent
            name="docType"
            label="Tipo de documento que requisita"
            control={control}
            errors={errors}
            options={[
              { value: DocumentTypeEnum.DECLARACAO, label: "Declaração" },
              { value: DocumentTypeEnum.CERTIFICADO, label: "Certificado" },
              { value: DocumentTypeEnum.DIPLOMA, label: "Diploma" },
              { value: DocumentTypeEnum.CREDENCIAL, label: DocumentTypeEnum.CREDENCIAL },
            ]}
            onSelect={handleChangeDocumentType}
          />
          <SelectComponent
            name="courseId"
            label="Cursos"
            control={control}
            errors={errors}
            required={true}
            options={courses?.map((course) => ({
              value: course.id,
              label: course.name
            }))}
          />
          <RadioGroupComponent name="payment.isPaymentByEntityReference" label="Tipo de emolumento" control={control} errors={errors}
            style={{ width: '50%' }}
            options={[
              { value: "true", label: "Entidade Referencia" }
              , { value: "false", label: "Comprovativo" }]}
            fieldProps={{
              style: { display: 'flex', flexDirection: 'row', gap: '16px' },
              onChange: handleOnChangePaymentType
            }}
          />
          <SelectComponent
            name="payment.idBankEntity"
            label="Entidade Bancária"
            control={control}
            errors={errors}
            itemsLoading={isLoadingSchoolschoolBankConfigs}
            itemsError={isErrorSchoolschoolBankConfigs}
            options={schoolBankConfigsData?.items?.filter(item => item.id == processModelsData?.items.at(0)?.idSchoolBankConfig).map(item => ({
              value: item.id!,
              label: String(item.bankEntity!) ?? "",
            })) ?? []}
            style={{ width: '100%' }}
            fieldProps={{ disabled: !stringToBoolean(watchPaymentType) }}
          />
          <MultiSelectComponent
            name="idsPaymentTypes"
            label="Tipo(s) de pagamento"
            control={control}
            errors={errors}
            style={{ width: "100%" }}
            options={
              paymentTypesData?.items
                ?.filter(item =>
                  processModelsData?.items?.[0]?.idsPaymentTypes
                    ?.split(",")
                    .includes(String(item.id))
                )
                .map(item => ({
                  value: item.id!,
                  label: String(item.description!)
                })) ?? []
            }
            itemsError={isErrorPaymentTypes}
            itemsLoading={isLoadingPaymentTypes}
            fieldProps={{ disabled: true }}
          />

          <Box>
            <Text as="label" size="2">Comprovativo de Pagamento</Text>
            <Controller
              name={`payment.paymentProof`}
              control={control}

              disabled={stringToBoolean(watchPaymentType)}
              render={({ field }) => (
                <DragAndDropFileUpload
                  onFileDrop={(file) => {
                    field.onChange(file);
                  }}
                  label={`Arraste ou selecione o arquivo`}
                  accept="*"
                  selectedFile={field.value}
                  isReadOnly={stringToBoolean(watchPaymentType)}
                  disabled={stringToBoolean(watchPaymentType)}
                />
              )}
            />
            {errors.payment?.paymentProof && (
              <Text color="red" size="1">{`${errors.payment.paymentProof.message}`}</Text>
            )}
          </Box>

          <RadioGroupComponent
            name="isUrgent"
            label="É Urgente?"
            control={control}
            errors={errors}
            options={[
              { value: "true", label: "Sim" },
              { value: "false", label: "Não" },
            ]}
          />
          <Flex direction='row' gap='4' style={{ alignSelf: 'end' }}>
            <Button color='red' onClick={handleBack} disabled={mutation.isLoading}>
              <ChevronLeftIcon /> voltar
            </Button>
            <Button type="submit" disabled={mutation.isLoading} variant='surface'  >
              {mutation.isLoading ? <Spinner loading> <EnterIcon /></Spinner> : <EnterIcon />}
              Registar
            </Button>
          </Flex>

        </Flex>
      </Card>

    </form>
  )
}
